// NOTE(felipe.brahm): Implementation based on https://github.com/dvtng/react-loading-skeleton,
//                     which I later realized was written by our own david.tang :)

import { CSSInterpolation } from '@emotion/css/create-instance'
import { keyframes } from '@emotion/react'
import React from 'react'

import { NeutralColorToken } from '../themes'
import { getColorValue, useTheme } from './ThemeProvider'

const ANIMATION_DURATION_IN_MS = 1000

// NOTE(felipe.brahm): We use viewport units so that the horizontal position of the shimmer moves along the whole page instead of per element.
const SKELETON_KEYFRAMES = keyframes({
  '0%': {
    backgroundPosition: '-100vw 0',
  },
  '100%': {
    backgroundPosition: '100vw 0',
  },
})

type BaseProps = {
  children?: React.ReactNode
  /**
   * Number of times you would like this Skeleton to repeat (useful when rendering multiple lines).
   */
  count?: number
  /**
   * The CSS `display` property for the rendered element.
   */
  display?: 'inline-block' | 'block'
  /**
   * A height with or without unit (ie. `50`, `"100%"`, etc.). When passing a number, it will use `px` as the unit.
   */
  height?: number | string
  /**
   * Useful when passing `children` into a `Skeleton`.
   * Set to false when you'd like to hide the `Skeleton` and render the children instead.
   */
  isLoading?: boolean
  /**
   * A width with or without unit (ie. `50`, `"100%"`, etc.). When passing a number, it will use `px` as the unit.
   */
  width?: number | string
  /**
   * Pass a CSS object if you need to override styles on the root element. Please do this sparingly.
   * When using `count` > 1, these styles will be applied to each individual `Skeleton`.
   */
  overrideRootStyles?: CSSInterpolation
}

type Props =
  | (BaseProps & {
      /**
       * Set to true if you would like to render a circle. You must specify a `width` and `height` for this to work.
       */
      isCircle?: false
    })
  // Make width and height required when isCircle=true
  | (BaseProps & {
      isCircle: true
      width: number | string
      height: number | string
    })

/**
 * Displays a "skeleton" of a component while it's loading or fetching data.
 * It can be used in many different ways to allow for flexibility when building complex layouts.
 *
 * It's often better to use a `Skeleton` rather than a `Spinner*` when you know the specific
 * shape of UI that will be rendered once the data is loaded.
 */
export function Skeleton({
  children,
  count = 1,
  display = 'inline-block',
  height,
  isCircle = false,
  isLoading = true,
  overrideRootStyles,
  width = '100%',
}: Props) {
  const theme = useTheme()
  const baseColor = getColorValue(NeutralColorToken.Bg3, theme)
  const highlightColor = getColorValue(NeutralColorToken.Bg2, theme)

  if (!isLoading) {
    return <>{children}</>
  }

  // NOTE(felipe.brahm): This adds a delay to the start of the animation so that all animations are in sync.
  //                     We accomplish this by making sure all animations start at the beggining of every second.
  const animationDelayInMs = -(new Date().getTime() % ANIMATION_DURATION_IN_MS)

  return (
    <>
      {Array(count)
        .fill(null)
        .map((_, i) => (
          <span
            key={i}
            aria-hidden="true"
            css={[
              {
                animation: `${SKELETON_KEYFRAMES} ${ANIMATION_DURATION_IN_MS}ms ease-in-out infinite`,
                animationDelay: `${animationDelayInMs}ms`,
                backgroundAttachment: 'fixed',
                backgroundColor: baseColor,
                backgroundImage: `linear-gradient(90deg, ${baseColor}, ${highlightColor}, ${baseColor})`,
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'cover',
                borderRadius:
                  width !== undefined && height !== undefined && isCircle
                    ? '50%'
                    : '4px',
                display,
                height,
                width,
              },
              overrideRootStyles,
            ]}
          >
            &zwnj;
          </span>
        ))}
    </>
  )
}
